<template>
  <div class="transition-[border-color] duration-250 ease-in-out">
    <TeaserDefault
      v-if="url && hasParent"
      :to="url"
      :title="title"
      :text="text"
      :media="media"
      :tag="tag"
      :has-parent="hasParent"
      :is-person="isPerson"
    />
    <TeaserFull
      v-else-if="url && !hasParent"
      :to="url"
      :title="title"
      :text="text"
      :media="media"
      :has-parent="hasParent"
      :tag="tag"
      :is-person="isPerson"
      :info-box-position="infoBoxPosition"
    />
    <TeaserNoLink
      v-else-if="hasParent"
      :title="title"
      :text="text"
      :media="media"
      :is-person="isPerson"
    />
    <TeaserNoLinkFull
      v-else-if="!hasParent"
      :title="title"
      :text="text"
      :media="media"
      :is-person="isPerson"
      :info-box-position="infoBoxPosition"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * This paragraph has a link field that allowed both internal and external links.
 * In case of external links, the title, text and media fields are required. For
 * internal links, they are optional.
 *
 * The title, text and media fields should always be used as the primary source.
 * If the link is internal we can fallback to the linked entity's teaser data.
 */
import type {
  FieldItemTypeLinkEntityFragment,
  MediaImageFragment,
  ParagraphTeaserFragment,
} from '#graphql-operations'
import { computed } from 'vue'

const props = defineProps<{
  title?: ParagraphTeaserFragment['title']
  link?: ParagraphTeaserFragment['link']
  text?: ParagraphTeaserFragment['text']
  image?: ParagraphTeaserFragment['image']
  hasParent?: boolean
  item?: Record<string, unknown>
  to?: string
}>()

const { parentType, options } = defineBlokkli({
  bundle: 'teaser',
  options: {
    infoBoxAlignment: {
      type: 'radios',
      label: 'Info box alignment',
      default: 'bottom',
      displayAs: 'icons',
      options: {
        top: {
          label: 'Ausrichtung oben',
          icon: 'icon-blokkli-option-box-top',
        },
        bottom: {
          label: 'Ausrichtung unten',
          icon: 'icon-blokkli-option-box-bottom',
        },
      },
    },
  },
  editor: {
    editTitle: (el) => el.querySelector('h3')?.innerText,
    mockProps: (text) => {
      return {
        overrideTitle: text || 'Teaser Titel',
        link: {
          uri: {
            path: '/',
          },
        },
      }
    },
    determineVisibleOptions: (ctx) => {
      const options: 'infoBoxAlignment'[] = []
      if (!ctx.parentType) {
        options.push('infoBoxAlignment')
      }
      return options
    },
  },
})

// Define a discriminated union type for all supported entity types
type EntityWithTeaser =
  | {
      __typename: 'NodePage' | 'NodeIntegrationPrograms'
      teaserTitle?: string
      teaserText?: string
      lead?: string
      teaserImage?: MediaImageFragment
      heroImage?: MediaImageFragment
      label?: string
      title?: string // For direct item prop from integration-programs
      image?: MediaImageFragment // For direct item prop from integration-programs
      tags?: Array<{
        name?: string
        isTeaser?: boolean
        label?: string
        uuid?: string // For direct item prop from integration-programs
      }>
      url?: {
        path?: string
      }
    }
  | {
      __typename: 'NodeEvent'
      teaserTitle?: string
      teaserText?: string
      lead?: string
      teaserImage?: MediaImageFragment
      heroImage?: MediaImageFragment
      label?: string
      title?: string
      image?: MediaImageFragment
      eventDate?: { formatted?: string }
      url?: {
        path?: string
      }
    }
  | {
      __typename: 'TaxonomyTermPerson'
      name: string
      description?: string
      function?: string
      department?: string
      area?: string
      email?: string
      image?: MediaImageFragment
      link?: {
        uri?: {
          path?: string
        }
      }
    }

// Single type guard to check entity type
function isValidEntity(entity: unknown): entity is EntityWithTeaser {
  return (
    typeof entity === 'object' &&
    entity !== null &&
    '__typename' in entity &&
    [
      'NodePage',
      'NodeIntegrationPrograms',
      'NodeEvent',
      'TaxonomyTermPerson',
    ].includes((entity as { __typename?: string }).__typename ?? '')
  )
}

// Type guard for EntityCanonicalUrl
function isEntityCanonicalUrl(uri: unknown): uri is {
  __typename: 'EntityCanonicalUrl'
  entity?: FieldItemTypeLinkEntityFragment
} {
  return (
    typeof uri === 'object' &&
    uri !== null &&
    '__typename' in uri &&
    (uri as { __typename?: string }).__typename === 'EntityCanonicalUrl'
  )
}

// Simplified linkedEntity computed property
const linkedEntity = computed<EntityWithTeaser | undefined>(() => {
  // If item prop exists, use it directly
  if (props.item) {
    // If item has __typename, check if it's a valid entity type
    if (props.item.__typename && isValidEntity(props.item)) {
      return props.item
    }

    // If item doesn't have __typename but has required properties,
    // create a compatible entity structure
    if (
      (typeof props.item?.title === 'string' ||
        typeof props.item?.label === 'string') &&
      props.item?.url &&
      typeof props.item.url === 'object' &&
      'path' in props.item.url
    ) {
      // Determine if this is likely an event based on the eventDate structure
      const isEvent =
        props.item?.eventDate &&
        typeof props.item.eventDate === 'object' &&
        'formatted' in props.item.eventDate

      if (isEvent) {
        return {
          __typename: 'NodeEvent',
          title: props.item.title || props.item.label,
          lead: props.item.lead,
          image: props.item.image,
          teaserImage: props.item.teaserImage,
          eventDate: props.item.eventDate,
          url: props.item.url,
        } as EntityWithTeaser
      } else {
        return {
          __typename: 'NodeIntegrationPrograms',
          title: props.item.title || props.item.label,
          lead: props.item.lead,
          image: props.item.image,
          teaserImage: props.item.teaserImage,
          tags: props.item.tags,
          url: props.item.url,
        } as EntityWithTeaser
      }
    }
  }

  // If no item prop, try to get entity from link
  const uri = props.link?.uri
  if (
    uri &&
    isEntityCanonicalUrl(uri) &&
    uri.entity &&
    isValidEntity(uri.entity)
  ) {
    return uri.entity
  }

  return undefined
})

const hasParent = computed(() => {
  if (props.hasParent) {
    return props.hasParent
  }
  return !!parentType.value
})

const isPerson = computed(() => {
  return linkedEntity.value?.__typename === 'TaxonomyTermPerson'
})

const infoBoxPosition = computed(() =>
  options.value.infoBoxAlignment.toString(),
)

const title = computed(() => {
  // Direct prop takes precedence
  if (props.title) {
    return props.title
  }

  if (!linkedEntity.value) {
    return ''
  }

  // Handle different entity types
  switch (linkedEntity.value.__typename) {
    case 'NodePage':
    case 'NodeIntegrationPrograms':
    case 'NodeEvent':
      return (
        linkedEntity.value.title ||
        linkedEntity.value.teaserTitle ||
        linkedEntity.value.label ||
        ''
      )
    case 'TaxonomyTermPerson':
      return linkedEntity.value.function
        ? `${linkedEntity.value.name}, ${linkedEntity.value.function}`
        : linkedEntity.value.name || ''
    default:
      return ''
  }
})

const text = computed(() => {
  // Direct prop takes precedence
  if (props.text) {
    return props.text
  }

  if (!linkedEntity.value) {
    return ''
  }

  // Handle different entity types
  switch (linkedEntity.value.__typename) {
    case 'NodePage':
    case 'NodeIntegrationPrograms':
    case 'NodeEvent':
      return linkedEntity.value.teaserText || linkedEntity.value.lead || ''
    case 'TaxonomyTermPerson': {
      return linkedEntity.value.description || ''
    }
    default:
      return ''
  }
})

const media = computed(() => {
  // Direct prop takes precedence
  if (props.image) {
    return props.image
  }

  if (!linkedEntity.value) {
    return undefined
  }

  // Handle different entity types
  switch (linkedEntity.value.__typename) {
    case 'NodePage':
    case 'NodeIntegrationPrograms':
    case 'NodeEvent':
      return (
        linkedEntity.value.image ||
        linkedEntity.value.teaserImage ||
        linkedEntity.value.heroImage
      )
    case 'TaxonomyTermPerson':
      return linkedEntity.value.image
    default:
      return undefined
  }
})

const tag = computed(() => {
  if (!linkedEntity.value) {
    return undefined
  }

  // Handle different entity types
  switch (linkedEntity.value.__typename) {
    case 'NodeIntegrationPrograms':
      // For NodeIntegrationPrograms, tags is an array of objects
      return linkedEntity.value.tags && Array.isArray(linkedEntity.value.tags)
        ? linkedEntity.value.tags.filter((i) => i?.isTeaser)?.[0]?.label ||
            undefined
        : undefined
    case 'NodeEvent':
      // For NodeEvent, eventDate is a formatted date string
      return linkedEntity.value.eventDate &&
        'formatted' in linkedEntity.value.eventDate
        ? linkedEntity.value.eventDate.formatted || undefined
        : undefined
    default:
      return undefined
  }
})

const url = computed(() => {
  // Direct 'to' prop takes precedence
  if (props.to) {
    return props.to
  }

  // If we have a linked entity, get URL based on entity type
  if (linkedEntity.value) {
    switch (linkedEntity.value.__typename) {
      case 'NodePage':
      case 'NodeIntegrationPrograms':
      case 'NodeEvent':
        return linkedEntity.value.url?.path || ''
      case 'TaxonomyTermPerson':
        return linkedEntity.value.link?.uri?.path || ''
      default:
        return ''
    }
  }

  // Fallback to link prop
  return props.link?.uri?.path || ''
})
</script>
